import { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../context/AuthContext'
import { getZodiacSign } from '../utils/zodiac'
import { uploadPhoto } from '../lib/storage'
import { getPhotoPublicUrl } from '../lib/profilePhotos'
import Loader from '../components/Loader'
import '../pages/onboarding/onboarding.css'
import '../pages/onboarding/photos.css'

const SECTIONS_INTERESTS = [
  { titleKey: 'onboarding.interestsSectionLifestyle', itemKeys: ['deepTalks', 'humor', 'personalGrowth', 'mindfulness', 'spirituality', 'lateNights', 'earlyMornings'] },
  { titleKey: 'onboarding.interestsSectionDating', itemKeys: ['meaningfulConnections', 'flirting', 'emotionalCloseness', 'slowDating', 'instantChemistry'] },
  { titleKey: 'onboarding.interestsSectionCulture', itemKeys: ['music', 'moviesSeries', 'artDesign', 'reading', 'photography'] },
  { titleKey: 'onboarding.interestsSectionActivities', itemKeys: ['traveling', 'walking', 'sportsFitness', 'yoga', 'dancing'] }
]

const compressImage = (file, maxW = 1280, maxH = 1280, q = 0.82) =>
  new Promise((resolve) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        let { width, height } = img
        if (width > height && width > maxW) { height = (height * maxW) / width; width = maxW }
        else if (height >= width && height > maxH) { width = (width * maxH) / height; height = maxH }
        canvas.width = Math.round(width)
        canvas.height = Math.round(height)
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
        resolve(canvas.toDataURL('image/jpeg', q))
      }
      img.onerror = () => resolve(e.target.result)
      img.src = e.target.result
    }
    reader.readAsDataURL(file)
  })

const days = Array.from({ length: 31 }, (_, i) => i + 1)
const months = [
  { v: 1, label: 'Jan' }, { v: 2, label: 'Feb' }, { v: 3, label: 'Mar' }, { v: 4, label: 'Apr' },
  { v: 5, label: 'May' }, { v: 6, label: 'Jun' }, { v: 7, label: 'Jul' }, { v: 8, label: 'Aug' },
  { v: 9, label: 'Sep' }, { v: 10, label: 'Oct' }, { v: 11, label: 'Nov' }, { v: 12, label: 'Dec' }
]
const years = Array.from({ length: new Date().getFullYear() - 1959 }, (_, i) => new Date().getFullYear() - i)

function isAdult(y, m, d) {
  const now = new Date()
  const birth = new Date(y, m - 1, d)
  let age = now.getFullYear() - birth.getFullYear()
  const mo = now.getMonth() - birth.getMonth()
  if (mo < 0 || (mo === 0 && now.getDate() < birth.getDate())) age--
  return age >= 18
}

const ProfileEdit = () => {
  const navigate = useNavigate()
  const { section } = useParams()
  const { t } = useTranslation()
  const { user, profile, updateProfile, refreshProfile } = useAuth()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // name
  const [name, setName] = useState('')
  // birth
  const [day, setDay] = useState('')
  const [month, setMonth] = useState('')
  const [year, setYear] = useState('')
  const [time, setTime] = useState('')
  const [place, setPlace] = useState('')
  // interests
  const [selectedInterests, setSelectedInterests] = useState([])
  // photos: mix of existing URLs and new base64 (new ones uploaded on save)
  const [photos, setPhotos] = useState([])
  const photoInputRefs = [useRef(null), useRef(null), useRef(null)]

  useEffect(() => {
    if (!user || !profile) return
    setName(profile.name || '')
    const dob = profile.date_of_birth
    if (dob) {
      const s = String(dob).trim().split('T')[0]
      const parts = s.split('-')
      if (parts.length === 3) {
        setYear(parts[0])
        setMonth(parts[1].replace(/^0/, '') || parts[1])
        setDay(parts[2].replace(/^0/, '') || parts[2])
      }
    } else {
      setDay('')
      setMonth('')
      setYear('')
    }
    const timeVal = profile.time_of_birth
    setTime(typeof timeVal === 'string' && timeVal.length >= 5 ? timeVal.slice(0, 5) : (timeVal || ''))
    setPlace(profile.place_of_birth || '')
    setSelectedInterests(Array.isArray(profile.interests) ? [...profile.interests] : [])
    if (section === 'photos') {
      const existing = profile.photos && profile.photos.length
        ? profile.photos.map((p) => (p && (p.startsWith('http') ? p : getPhotoPublicUrl(p))) || null)
        : []
      setPhotos([existing[0] || null, existing[1] || null, existing[2] || null])
    }
  }, [user, profile, section])

  const onBack = () => {
    navigate('/app/profile')
  }

  const saveName = async () => {
    const trimmed = name.trim()
    if (!trimmed) { setError(t('onboarding.nameRequired')); return }
    if (trimmed.length > 30) { setError(t('onboarding.nameMaxLength')); return }
    setError('')
    setLoading(true)
    const { error: err } = await updateProfile(user.id, { name: trimmed })
    setLoading(false)
    if (err) setError(err)
    else { await refreshProfile(); navigate('/app/profile') }
  }

  const saveBirth = async () => {
    if (!day || !month || !year) { setError(t('onboarding.birthDateRequired')); return }
    if (!isAdult(Number(year), Number(month), Number(day))) { setError(t('onboarding.birthAdultRequired')); return }
    setError('')
    const dateOfBirth = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    const timeOfBirth = time && time.trim() ? time.trim() : null
    const placeOfBirth = place && place.trim() ? place.trim() : null
    const zodiac = getZodiacSign(Number(day), Number(month))
    setLoading(true)
    const updates = { date_of_birth: dateOfBirth, time_of_birth: timeOfBirth, place_of_birth: placeOfBirth, zodiac_sign: zodiac.name }
    const { error: err } = await updateProfile(user.id, updates)
    setLoading(false)
    if (err) setError(err)
    else { await refreshProfile(); navigate('/app/profile') }
  }

  const saveInterests = async () => {
    setLoading(true)
    const { error: err } = await updateProfile(user.id, { interests: selectedInterests })
    setLoading(false)
    if (err) setError(err)
    else { await refreshProfile(); navigate('/app/profile') }
  }

  const saveGender = async () => {
    if (!gender) { setError(t('profileEdit.selectGender')); return }
    setError('')
    setLoading(true)
    const { error: err } = await updateProfile(user.id, { gender })
    setLoading(false)
    if (err) setError(err)
    else { await refreshProfile(); navigate('/app/profile') }
  }

  const saveLookingFor = async () => {
    const value = lookingFor === 'men' ? 'male' : lookingFor === 'women' ? 'female' : lookingFor || 'both'
    setLoading(true)
    const { error: err } = await updateProfile(user.id, { looking_for: value })
    setLoading(false)
    if (err) setError(err)
    else { await refreshProfile(); navigate('/app/profile') }
  }

  const savePhotos = async () => {
    const hasAny = photos.some((p) => p && (p.startsWith('http') || p.startsWith('data:')))
    if (!hasAny) { setError(t('profileEdit.atLeastOnePhotoRequired')); return }
    setError('')
    setLoading(true)
    try {
      const allUrls = []
      for (const p of photos) {
        if (!p) continue
        if (p.startsWith('http')) {
          allUrls.push(p)
        } else if (p.startsWith('data:')) {
          const url = await uploadPhoto(p, user.id, allUrls.length, { uniquePath: true })
          if (url) allUrls.push(url)
        }
      }
      if (allUrls.length < 1) { setError(t('profileEdit.failedToUploadPhotos')); setLoading(false); return }
      const { error: err } = await updateProfile(user.id, { photos: allUrls })
      if (err) setError(err)
      else {
        await refreshProfile()
        navigate('/app/profile', { replace: true })
      }
    } catch (e) {
      setError(e?.message || t('profileEdit.uploadFailed'))
    }
    setLoading(false)
  }

  const toggleInterest = (key) => {
    setSelectedInterests((prev) => (prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]))
  }

  const onPhotoFile = async (idx, file) => {
    if (!file) return
    const dataUrl = await compressImage(file)
    if (!dataUrl) return
    setPhotos((prev) => {
      const next = prev.length ? [...prev] : [null, null, null]
      while (next.length <= idx) next.push(null)
      next[idx] = dataUrl
      return next.slice(0, 3)
    })
    const input = photoInputRefs[idx]?.current
    if (input) {
      input.value = ''
      input.type = ''
      input.type = 'file'
    }
  }

  const removePhoto = (idx) => {
    const next = [...photos]
    next[idx] = null
    setPhotos(next.slice(0, 3))
  }

  if (!user || !profile) return <div style={{ padding: 24 }}><Loader text={t('common.loading')} /></div>

  const validSections = ['name', 'birth', 'interests', 'photos']
  if (!validSections.includes(section)) {
    navigate('/app/profile')
    return null
  }

  const header = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
      <button type="button" className="link" onClick={onBack}>{t('profileEdit.back')}</button>
    </div>
  )

  if (section === 'name') {
    return (
      <div className="onboarding-page">
        <div className="onboarding-content" style={{ paddingTop: 48, paddingBottom: 100 }}>
          {header}
          <h1 className="h1">{t('profileEdit.nameTitle')}</h1>
          <p className="p">{t('onboarding.nameSubtitle')}</p>
          <div className="field">
            <label className="label">{t('onboarding.nameLabel')}</label>
            <input className={`input ${error ? 'error' : ''}`} value={name} onChange={(e) => { setName(e.target.value); setError('') }} maxLength={30} placeholder={t('onboarding.namePlaceholder')} />
            {error && <div className="error-text">{error}</div>}
          </div>
          <button className="cta-fixed" onClick={saveName} disabled={loading}>{loading ? <Loader text={t('common.saving')} inline /> : t('common.save')}</button>
        </div>
      </div>
    )
  }

  if (section === 'birth') {
    return (
      <div className="onboarding-page">
        <div className="onboarding-content" style={{ paddingTop: 48, paddingBottom: 100 }}>
          {header}
          <h1 className="h1">{t('onboarding.birthTitle')}</h1>
          <p className="p">{t('profileEdit.birthSubtitle')}</p>
          <div className="field">
            <div className="label">{t('onboarding.birthDateLabel')}</div>
            <div className="row">
              <select className="select" value={day} onChange={(e) => setDay(e.target.value)}>
                <option value="">{t('onboarding.birthDay')}</option>
                {days.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
              <select className="select" value={month} onChange={(e) => setMonth(e.target.value)}>
                <option value="">{t('onboarding.birthMonth')}</option>
                {months.map((m) => <option key={m.v} value={m.v}>{m.label}</option>)}
              </select>
              <select className="select" value={year} onChange={(e) => setYear(e.target.value)}>
                <option value="">{t('onboarding.birthYear')}</option>
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>
          <div className="field">
            <label className="label">{t('onboarding.birthTimeLabel')}</label>
            <input className="input" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
          </div>
          <div className="field">
            <label className="label">{t('onboarding.birthPlaceLabel')}</label>
            <input className="input" value={place} onChange={(e) => setPlace(e.target.value)} placeholder={t('profileEdit.placePlaceholder')} />
          </div>
          {error && <div className="error-text">{error}</div>}
          <button className="cta-fixed" onClick={saveBirth} disabled={loading}>{loading ? <Loader text={t('common.saving')} inline /> : t('common.save')}</button>
        </div>
      </div>
    )
  }

  if (section === 'interests') {
    return (
      <div className="onboarding-page">
        <div className="onboarding-content" style={{ paddingTop: 48, paddingBottom: 100 }}>
          {header}
          <h1 className="h1">{t('profile.interests')}</h1>
          <p className="p">{t('onboarding.interestsSubtitle')}</p>
          {SECTIONS_INTERESTS.map((s) => (
            <div key={s.titleKey} className="field">
              <div className="label">{t(s.titleKey)}</div>
              <div className="chips">
                {s.itemKeys.map((key) => (
                  <button key={key} className={`chip ${selectedInterests.includes(key) ? 'active' : ''}`} onClick={() => toggleInterest(key)} type="button">{t('onboarding.interestsItems.' + key)}</button>
                ))}
              </div>
            </div>
          ))}
          <button className="cta-fixed" onClick={saveInterests} disabled={loading}>{loading ? <Loader text={t('common.saving')} inline /> : t('common.save')}</button>
        </div>
      </div>
    )
  }

  if (section === 'photos') {
    const currentPhotos = photos.length ? photos : [null, null, null]
    return (
      <div className="onboarding-page">
        <div className="onboarding-content" style={{ paddingTop: 48, paddingBottom: 100 }}>
          {header}
          <h1 className="h1">{t('profileEdit.photosTitle')}</h1>
          <p className="p">{t('profileEdit.photosSubtitle')}</p>
          <div className={`photo-row ${error ? 'error' : ''}`}>
            {currentPhotos.slice(0, 3).map((p, idx) => (
              <div key={`card-${idx}`} className="photo-card" onClick={() => photoInputRefs[idx]?.current?.click()} role="button" tabIndex={0}>
                <input ref={photoInputRefs[idx]} type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => onPhotoFile(idx, e.target.files?.[0])} />
                {p ? (
                  <>
                    <img
                      key={`img-${idx}-${p ? (p.startsWith('data:') ? p.slice(-80) : p) : 'e'}`}
                      src={p}
                      alt=""
                      className="photo-card-img"
                    />
                    <button type="button" className="photo-remove" onClick={(e) => { e.stopPropagation(); removePhoto(idx) }} aria-label={t('onboarding.photosRemovePhoto')}>×</button>
                  </>
                ) : (
                  <span className="photo-plus">+</span>
                )}
              </div>
            ))}
          </div>
          {error && <div className="error-text">{error}</div>}
          <button className="cta-fixed" onClick={savePhotos} disabled={loading}>{loading ? <Loader text={t('common.saving')} inline /> : t('common.save')}</button>
        </div>
      </div>
    )
  }

  return null
}

export default ProfileEdit
